import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, RefreshCw, ArrowRight, BookmarkPlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { HAR_MARKET_GROUPS, HOUSTON_SUB_MARKETS, HOUSTON_SUB_MARKET_ZIPS } from "../lib/harMarketAreas";
import Seo from "../components/Seo";
import IdxDisclaimer from "../components/IdxDisclaimer";
import SaveHeart from "../components/SaveHeart";
import HoverCyclePhoto from "../components/HoverCyclePhoto";

const TYPES = [
  "All",
  "Single Family Residence",
  "Condominium",
  "Mid/Hi-Rise Condominium",
  "Townhouse",
  "Duplex",
  "Multi-Family",
  "Country Homes/Acreage",
  "Residential Lots & Land",
];
const BEDS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
const BATHS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
const GARAGES = [0, 1, 2, 3, 4, 5];
const CONDITIONS = [
  { value: "", label: "Any condition" },
  { value: "Never Lived In", label: "New — never lived in" },
  { value: "Under Construction", label: "Under construction" },
  { value: "To Be Built", label: "To be built" },
  { value: "Existing", label: "Existing / resale" },
];
const SORTS = [
  { value: "newest", label: "Newest first" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "sqft_desc", label: "Largest home (sqft)" },
  { value: "lot_desc", label: "Largest lot" },
  { value: "year_desc", label: "Newest construction" },
  { value: "year_asc", label: "Oldest first" },
];
const PAGE_SIZE = 24;

const fmt = (n) => (n ? `$${Number(n).toLocaleString()}` : "—");

export default function Properties() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, login } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [lastLoaded, setLastLoaded] = useState(null);

  const [city, setCity] = useState(searchParams.get("city") || "All");
  const [type, setType] = useState(searchParams.get("property_type") || "All");
  const [minPrice, setMinPrice] = useState(searchParams.get("min_price") || "");
  const [maxPrice, setMaxPrice] = useState(searchParams.get("max_price") || "");
  const [beds, setBeds] = useState(Number(searchParams.get("beds") || 0));
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [listingType, setListingType] = useState(searchParams.get("listing_type") || "sale");
  const [bathsFilter, setBathsFilter] = useState(Number(searchParams.get("baths") || 0));
  const [minSqft, setMinSqft] = useState(searchParams.get("min_sqft") || "");
  const [maxSqft, setMaxSqft] = useState(searchParams.get("max_sqft") || "");
  const [minGarage, setMinGarage] = useState(Number(searchParams.get("min_garage") || 0));
  const [newConstruction, setNewConstruction] = useState(searchParams.get("new_construction") === "true");
  const [condition, setCondition] = useState(searchParams.get("property_condition") || "");
  const [sort, setSort] = useState(searchParams.get("sort") || "newest");
  const [subdivision, setSubdivision] = useState(searchParams.get("subdivision") || "");
  const [subdivisionOptions, setSubdivisionOptions] = useState([]);

  // Load facets (subdivisions) once
  useEffect(() => {
    api.get("/mls/facets").then((r) => setSubdivisionOptions(r.data?.subdivisions || [])).catch(() => {});
  }, []);

  const buildParams = useCallback(
    (skip = 0) => {
      const params = { limit: PAGE_SIZE, skip, status: "Active", listing_type: listingType, sort };
      // Houston sub-markets (Alief, Sharpstown, Heights…) live inside city="Houston"
      // in MLS records — map each sub-market to its known HAR zip codes so filtering
      // is geographically accurate instead of relying on text search.
      if (city !== "All") {
        if (HOUSTON_SUB_MARKETS.has(city)) {
          const zips = HOUSTON_SUB_MARKET_ZIPS[city];
          if (zips?.length) {
            params.zip_codes = zips.join(",");
          } else {
            params.q = q ? `${q} ${city}` : city;
          }
          if (q && !params.q) params.q = q;
        } else {
          params.city = city;
          if (q) params.q = q;
        }
      } else if (q) {
        params.q = q;
      }
      if (type !== "All") params.property_type = type;
      if (minPrice) params.min_price = Number(minPrice);
      if (maxPrice) params.max_price = Number(maxPrice);
      if (beds > 0) params.beds = beds;
      if (bathsFilter > 0) params.baths = bathsFilter;
      if (minSqft) params.min_sqft = Number(minSqft);
      if (maxSqft) params.max_sqft = Number(maxSqft);
      if (minGarage > 0) params.min_garage = minGarage;
      if (newConstruction) params.new_construction = true;
      if (condition) params.property_condition = condition;
      if (subdivision) params.subdivision = subdivision;
      return params;
    },
    [city, type, minPrice, maxPrice, beds, q, listingType, bathsFilter, minSqft, maxSqft, minGarage, newConstruction, condition, sort, subdivision]
  );

  const fetchList = useCallback(
    async ({ append = false, manual = false } = {}) => {
      if (manual) setRefreshing(true);
      if (!append) setLoading(true);
      try {
        const params = buildParams(append ? (page + 1) * PAGE_SIZE : 0);
        const { data } = await api.get("/mls/listings", { params });
        setTotal(data.total || 0);
        setItems((prev) => (append ? [...prev, ...(data.items || [])] : data.items || []));
        setLastLoaded(new Date());
      } catch {
        if (!append) setItems([]);
      } finally {
        setLoading(false);
        if (manual) setRefreshing(false);
      }
    },
    [buildParams, page]
  );

  // Load whenever filters change (reset paging)
  useEffect(() => {
    setPage(0);
    fetchList({ append: false });
    // sync URL
    const sp = new URLSearchParams();
    if (q) sp.set("q", q);
    if (city !== "All") sp.set("city", city);
    if (type !== "All") sp.set("property_type", type);
    if (minPrice) sp.set("min_price", minPrice);
    if (maxPrice) sp.set("max_price", maxPrice);
    if (beds) sp.set("beds", String(beds));
    if (bathsFilter) sp.set("baths", String(bathsFilter));
    if (minSqft) sp.set("min_sqft", minSqft);
    if (maxSqft) sp.set("max_sqft", maxSqft);
    if (minGarage) sp.set("min_garage", String(minGarage));
    if (newConstruction) sp.set("new_construction", "true");
    if (condition) sp.set("property_condition", condition);
    if (subdivision) sp.set("subdivision", subdivision);
    if (sort && sort !== "newest") sp.set("sort", sort);
    if (listingType && listingType !== "sale") sp.set("listing_type", listingType);
    setSearchParams(sp, { replace: true });
  }, [q, city, type, minPrice, maxPrice, beds, listingType, bathsFilter, minSqft, maxSqft, minGarage, newConstruction, condition, sort, subdivision]);

  // Auto-refresh every 5 min
  useEffect(() => {
    const t = setInterval(() => fetchList({ append: false }), 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [fetchList]);

  const clear = () => {
    setQ("");
    setCity("All");
    setType("All");
    setMinPrice("");
    setMaxPrice("");
    setBeds(0);
    setBathsFilter(0);
    setMinSqft("");
    setMaxSqft("");
    setMinGarage(0);
    setNewConstruction(false);
    setCondition("");
    setSubdivision("");
    setSort("newest");
  };

  const loadMore = () => {
    setPage((p) => p + 1);
    fetchList({ append: true });
  };

  const hasMore = items.length < total;

  const saveThisSearch = async () => {
    if (!user) {
      toast("Sign in to save searches", {
        description: "We'll email you when new homes match.",
        action: { label: "Sign in", onClick: () => login() },
      });
      return;
    }
    const defaultName =
      (city !== "All" ? `${city} · ` : "") +
      (listingType === "rental" ? "Rentals" : "For Sale") +
      (beds ? ` · ${beds}+ bd` : "") +
      (maxPrice ? ` · <$${Number(maxPrice).toLocaleString()}` : "");
    const name = window.prompt("Name this saved search:", defaultName);
    if (!name) return;
    const filters = buildParams(0);
    delete filters.limit;
    delete filters.skip;
    try {
      await api.post("/saved-searches", { name, filters, notify: true }, { withCredentials: true });
      toast.success("Search saved", {
        description: "You'll get a weekly email when new homes match.",
      });
    } catch {
      toast.error("Could not save search. Please try again.");
    }
  };

  return (
    <div className="bg-stone-50 min-h-screen" data-testid="properties-page">
      <Seo
        title="Houston Homes for Sale"
        description="Browse thousands of Houston homes for sale live from the HAR MLS. Filter by market area, price, beds, and property type across Greater Houston."
        path="/properties"
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-12">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-px bg-[#C9A961]" />
          <div className="eyebrow text-[#0A0A0A] flex items-center gap-2 flex-wrap">
            Live · HAR MLS · Greater Houston
            {lastLoaded && (
              <span className="inline-flex items-center gap-1 text-gray-500 normal-case tracking-normal text-[10px]">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                Updated {lastLoaded.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </div>
        </div>
        <h1 className="font-display text-5xl lg:text-7xl leading-[0.95] tracking-tight text-[#0A0A0A] font-light">
          {listingType === "rental" ? (
            <>Homes for <span className="serif-italic text-[#E63946]">rent.</span></>
          ) : (
            <>Browse <span className="serif-italic text-[#E63946]">properties.</span></>
          )}
        </h1>
        <p className="text-[#555] mt-6 max-w-2xl text-lg font-light">
          Live listings from the Houston Association of REALTORS® MLS, refreshed nightly and every 5 minutes on this page.
        </p>

        {/* Buy / Rent toggle */}
        <div className="mt-8 inline-flex bg-white border border-gray-200 rounded-full p-1" role="tablist">
          <button
            type="button"
            onClick={() => setListingType("sale")}
            className={`px-5 py-2 rounded-full text-sm font-semibold uppercase tracking-[0.1em] transition-colors ${
              listingType === "sale" ? "bg-[#1A1A1A] text-white" : "text-[#1A1A1A] hover:bg-stone-50"
            }`}
            data-testid="properties-toggle-buy"
          >
            For Sale
          </button>
          <button
            type="button"
            onClick={() => setListingType("rental")}
            className={`px-5 py-2 rounded-full text-sm font-semibold uppercase tracking-[0.1em] transition-colors ${
              listingType === "rental" ? "bg-[#E63946] text-white" : "text-[#1A1A1A] hover:bg-stone-50"
            }`}
            data-testid="properties-toggle-rent"
          >
            For Rent
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="sticky top-20 z-30 bg-stone-50/95 backdrop-blur-md border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center gap-3 flex-wrap">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search address, city, or ZIP..."
            className="flex-1 min-w-[12rem] bg-white border border-gray-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-[#E63946]"
            data-testid="properties-search-input"
          />
          <button
            onClick={() => setFiltersOpen((v) => !v)}
            className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-4 py-2.5 text-sm font-medium hover:border-[#E63946]"
            data-testid="properties-filter-toggle"
          >
            <SlidersHorizontal className="w-4 h-4" /> Filters
          </button>
          <button
            onClick={() => fetchList({ manual: true })}
            disabled={refreshing}
            className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-4 py-2.5 text-sm font-medium hover:border-[#E63946] hover:text-[#E63946] disabled:opacity-60"
            data-testid="properties-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
          {(city !== "All" || type !== "All" || minPrice || maxPrice || beds || bathsFilter || minSqft || maxSqft || minGarage || newConstruction || condition || subdivision || q) && (
            <button
              onClick={clear}
              className="flex items-center gap-1 text-xs text-gray-600 hover:text-[#E63946]"
              data-testid="properties-clear"
            >
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          )}
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-white border border-gray-200 rounded-md px-3 py-2.5 text-xs font-medium hover:border-[#E63946]"
            data-testid="properties-sort"
          >
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <button
            onClick={saveThisSearch}
            className="flex items-center gap-2 bg-white border border-gray-200 rounded-md px-4 py-2.5 text-sm font-medium hover:border-[#E63946] hover:text-[#E63946]"
            data-testid="properties-save-search"
            title="Save this search — get a weekly email when new homes match"
          >
            <BookmarkPlus className="w-4 h-4" /> Save this search
          </button>
          <div className="ml-auto text-xs uppercase tracking-[0.18em] text-gray-500">
            {loading && items.length === 0 ? "Loading…" : `${total.toLocaleString()} listing${total === 1 ? "" : "s"}`}
          </div>
        </div>

        {filtersOpen && (
          <div className="border-t border-gray-200 bg-white">
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <FilterField label="Market Area">
                <select
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full bg-transparent text-sm font-medium focus:outline-none"
                  data-testid="filter-city"
                >
                  <option value="All">All Houston Metro</option>
                  {HAR_MARKET_GROUPS.map((g) => (
                    <optgroup key={g.label} label={g.label}>
                      {g.cities.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </FilterField>
              <FilterField label="Neighborhood / Subdivision">
                <input
                  type="text"
                  list="subdivision-options"
                  value={subdivision}
                  onChange={(e) => setSubdivision(e.target.value)}
                  placeholder="e.g. River Oaks"
                  className="w-full bg-transparent text-sm font-medium focus:outline-none"
                  data-testid="filter-subdivision"
                />
                <datalist id="subdivision-options">
                  {subdivisionOptions.slice(0, 400).map((s) => <option key={s} value={s} />)}
                </datalist>
              </FilterField>
              <FilterField label="Property Type">
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="w-full bg-transparent text-sm font-medium focus:outline-none"
                  data-testid="filter-type"
                >
                  {TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </FilterField>
              <FilterField label="Condition">
                <select
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                  className="w-full bg-transparent text-sm font-medium focus:outline-none"
                  data-testid="filter-condition"
                >
                  {CONDITIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </FilterField>
              <FilterField label="Min Beds">
                <select
                  value={beds}
                  onChange={(e) => setBeds(Number(e.target.value))}
                  className="w-full bg-transparent text-sm font-medium focus:outline-none"
                  data-testid="filter-beds"
                >
                  {BEDS.map((b) => <option key={b} value={b}>{b === 0 ? "Any" : b === 9 ? "9+" : `${b}+`}</option>)}
                </select>
              </FilterField>
              <FilterField label="Min Baths">
                <select
                  value={bathsFilter}
                  onChange={(e) => setBathsFilter(Number(e.target.value))}
                  className="w-full bg-transparent text-sm font-medium focus:outline-none"
                  data-testid="filter-baths"
                >
                  {BATHS.map((b) => <option key={b} value={b}>{b === 0 ? "Any" : b === 9 ? "9+" : `${b}+`}</option>)}
                </select>
              </FilterField>
              <FilterField label="Min Garages">
                <select
                  value={minGarage}
                  onChange={(e) => setMinGarage(Number(e.target.value))}
                  className="w-full bg-transparent text-sm font-medium focus:outline-none"
                  data-testid="filter-garage"
                >
                  {GARAGES.map((g) => <option key={g} value={g}>{g === 0 ? "Any" : `${g}+ car`}</option>)}
                </select>
              </FilterField>
              <FilterField label="New Construction Only">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newConstruction}
                    onChange={(e) => setNewConstruction(e.target.checked)}
                    className="w-4 h-4 accent-[#E63946]"
                    data-testid="filter-new-construction"
                  />
                  <span className="text-sm font-medium">Show only new builds</span>
                </label>
              </FilterField>
              <FilterField label="Min Price">
                <input
                  type="number"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  placeholder="$"
                  className="w-full bg-transparent text-sm font-medium focus:outline-none"
                  data-testid="filter-min-price"
                />
              </FilterField>
              <FilterField label="Max Price">
                <input
                  type="number"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  placeholder="$"
                  className="w-full bg-transparent text-sm font-medium focus:outline-none"
                  data-testid="filter-max-price"
                />
              </FilterField>
              <FilterField label="Min Sqft">
                <input
                  type="number"
                  value={minSqft}
                  onChange={(e) => setMinSqft(e.target.value)}
                  placeholder="1000"
                  className="w-full bg-transparent text-sm font-medium focus:outline-none"
                  data-testid="filter-min-sqft"
                />
              </FilterField>
              <FilterField label="Max Sqft">
                <input
                  type="number"
                  value={maxSqft}
                  onChange={(e) => setMaxSqft(e.target.value)}
                  placeholder="5000"
                  className="w-full bg-transparent text-sm font-medium focus:outline-none"
                  data-testid="filter-max-sqft"
                />
              </FilterField>
            </div>
          </div>
        )}
      </div>

      {/* Listings grid */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        {loading && items.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-gray-200" />
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-gray-200 rounded w-1/3" />
                  <div className="h-4 bg-gray-200 rounded w-2/3" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-24">
            <div className="font-display text-3xl text-[#1A1A1A]">No listings match yet.</div>
            <p className="text-[#555] mt-3 max-w-lg mx-auto">
              Try clearing filters or expanding your market-area choice.
            </p>
            <button
              onClick={clear}
              className="mt-6 bg-[#E63946] hover:bg-[#c9313d] text-white px-6 py-3 rounded-md font-semibold text-sm"
              data-testid="properties-empty-clear"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((l) => (
                <Link
                  key={l.listing_key}
                  to={`/mls-listings/${l.listing_key}`}
                  className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow group"
                  data-testid={`property-card-${l.listing_key}`}
                >
                  <div className="relative aspect-[4/3] bg-stone-100">
                    <HoverCyclePhoto photos={l.photos || []} alt={l.address} className="w-full h-full object-cover" />
                    <div className="absolute top-3 left-3 bg-white/90 backdrop-blur px-2.5 py-1 rounded text-[10px] uppercase tracking-[0.15em] font-semibold">
                      {l.status}
                    </div>
                    <SaveHeart
                      listingKey={l.listing_key}
                      address={l.address}
                      className="absolute top-3 right-3"
                    />
                  </div>
                  <div className="p-5">
                    <div className="font-display text-2xl font-semibold">{fmt(l.price)}</div>
                    <div className="text-sm text-[#1A1A1A] mt-1">{l.address}</div>
                    <div className="text-xs text-gray-500">{l.city}, {l.state} {l.zip || ""}</div>
                    <div className="text-xs text-gray-600 mt-2">
                      {l.beds} bd · {l.baths} ba · {l.sqft ? Number(l.sqft).toLocaleString() : "—"} sqft
                    </div>
                    <div className="text-[10px] text-gray-400 mt-2 truncate">
                      {l.list_office} · MLS #{l.listing_key}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            {hasMore && (
              <div className="mt-10 flex justify-center">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="bg-[#1A1A1A] hover:bg-[#333] text-white px-8 py-3 rounded-md text-sm font-semibold flex items-center gap-2 disabled:opacity-60"
                  data-testid="properties-load-more"
                >
                  {loading ? <><RefreshCw className="w-4 h-4 animate-spin" /> Loading…</> : <>Load more <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            )}
          </>
        )}

        <div className="mt-16 text-[10px] uppercase tracking-[0.2em] text-gray-400 text-center leading-relaxed">
          IDX data © Houston Association of REALTORS® · Delivered via CoreLogic Trestle · Refreshed nightly
        </div>
        <IdxDisclaimer />
      </div>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div className="bg-stone-50 rounded-md px-4 py-2.5">
      <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
